import React from 'react';
import { X } from 'lucide-react';
import { TAMANOS } from '../../constants/geografia';

const GRUPOS_MERCADO = [
  { value: 'veolia',                label: 'Veolia' },
  { value: 'aqualia',               label: 'Aqualia' },
  { value: 'acciona',               label: 'Acciona' },
  { value: 'global_omnium',         label: 'Global Omnium' },
  { value: 'facsa',                 label: 'FACSA' },
  { value: 'saur',                  label: 'SAUR' },
  { value: 'sacyr',                 label: 'Sacyr' },
  { value: 'municipal',             label: 'Municipal' },
  { value: 'independiente_publico', label: 'Independiente público' },
  { value: 'independiente_privado', label: 'Independiente privado' },
  { value: 'canal_isabel_ii',       label: 'Canal Isabel II' },
  { value: 'aqlara',                label: 'Aqlara' },
  { value: 'sumem',                 label: 'SUMEM' },
  { value: 'aigues_terrassa',       label: 'Aigües Terrassa' },
  { value: 'sin_datos',             label: 'Sin datos' },
];

const RANGOS_POB = [
  { value: 'muy_grande', label: '> 100.000 hab' },
  { value: 'grande',     label: '20.000 – 100.000' },
  { value: 'mediano',    label: '5.000 – 20.000' },
  { value: 'pequeno',    label: '1.000 – 5.000' },
  { value: 'muy_pequeno',label: '< 1.000' },
];

// Sector del agua: selección única (radio).
const SECTORES_AGUA = [
  { value: 'todos',          label: 'Todos' },
  { value: 'ciclo_integral', label: 'Ciclo integral' },
  { value: 'alta',           label: 'Agua potable — Alta' },
  { value: 'baja',           label: 'Agua potable — Baja' },
  { value: 'alta_baja',      label: 'Agua potable — Alta y Baja' },
  { value: 'saneamiento',    label: 'Saneamiento' },
];

// Rol del operador: selección múltiple (checkbox).
const ROLES_OPERADOR = [
  { value: 'titular_publico',   label: 'Titular público' },
  { value: 'operador_titular',  label: 'Operador = Titular (gestión directa)' },
  { value: 'concesionado',      label: 'Operador concesionado' },
  { value: 'empresa_publica_op',label: 'Empresa pública operadora' },
  { value: 'empresa_mixta',     label: 'Empresa mixta' },
  { value: 'ute',               label: 'UTE' },
];

// Naturaleza del operador: selección múltiple (checkbox).
const NATURALEZAS_OPERADOR = [
  { value: 'publico', label: 'Público (100% capital público)' },
  { value: 'privado', label: 'Privado' },
  { value: 'mixto',   label: 'Mixto' },
];

function toggle(arr, val) {
  return arr.includes(val) ? arr.filter((v) => v !== val) : [...arr, val];
}

const SECTION_TITLE = 'text-[10px] font-semibold text-ink-400 uppercase tracking-wider mb-2';
const CHECK_ROW = 'flex items-center gap-2 text-sm text-ink-600 cursor-pointer select-none';

// Selección múltiple con buscador interno, "Todas"/"Ninguna" y contador en el label.
function MultiSelect({ label, options, value, onChange }) {
  const [search, setSearch] = React.useState('');
  const filtered = search
    ? options.filter((o) => o.toLowerCase().includes(search.toLowerCase()))
    : options;
  const n = value.length;
  return (
    <div>
      <div className={SECTION_TITLE}>
        {label}{n > 0 ? ` (${n} seleccionada${n === 1 ? '' : 's'})` : ''}
      </div>
      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={`Buscar ${label.toLowerCase()}…`}
        className="w-full border border-ink-200 rounded-md px-2.5 py-1.5 text-sm bg-white mb-1.5"
      />
      <div className="flex gap-3 mb-1.5">
        <button type="button" onClick={() => onChange([...options])}
          className="text-[11px] text-ink-400 hover:text-ink-600 underline">Todas</button>
        <button type="button" onClick={() => onChange([])}
          className="text-[11px] text-ink-400 hover:text-ink-600 underline">Ninguna</button>
      </div>
      <div className="max-h-48 overflow-y-auto space-y-1 pr-1">
        {filtered.map((o) => (
          <label key={o} className={CHECK_ROW}>
            <input
              type="checkbox"
              checked={value.includes(o)}
              onChange={() => onChange(toggle(value, o))}
              className="rounded border-ink-300"
            />
            <span className="truncate">{o}</span>
          </label>
        ))}
        {filtered.length === 0 && (
          <div className="text-[11px] text-ink-300 italic px-1">Sin resultados</div>
        )}
      </div>
    </div>
  );
}

export default function MapaDrawerFiltros({
  isOpen, onClose, modo,
  capaProvincias, setCapaProvincias,
  capaCCAA, setCapaCCAA,
  capaZonas, setCapaZonas,
  sectorAgua, setSectorAgua,
  rolOperador, setRolOperador,
  naturalezaOperador, setNaturalezaOperador,
  gruposFilter, setGruposFilter,
  rangoFilter, setRangoFilter,
  countsPorRango,
  ccaaOptions, provinciaOptions,
  ccaasFilter, setCcaasFilter,
  provinciasFilter, setProvinciasFilter,
  tamano, setTamano,
  zonas, zonaFilter, setZonaFilter,
}) {
  if (!isOpen) return null;

  const esMercado = modo === 'mercado';

  // Posicionado RELATIVO al contenedor del mapa (div.relative), no al viewport:
  // la app está centrada (max-w-7xl, sin sidebar), así que un left/right fijo del
  // viewport no alinea con el mapa. z-[1100/1101] queda sobre los controles Leaflet (z-1000).
  return (
    <>
      {/* overlay */}
      <div
        data-testid="mapa-filtros-overlay"
        className="absolute inset-0 bg-black/20 z-[1100]"
        onClick={onClose}
      />
      {/* panel — sale desde el borde izquierdo del mapa */}
      <div
        data-testid="mapa-filtros-drawer"
        className="absolute left-0 top-0 bottom-0 z-[1101] w-[300px] bg-white shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-ink-100 bg-white shrink-0">
          <span className="font-semibold text-ink-900 text-sm">Filtros</span>
          <button onClick={onClose} className="p-1 rounded hover:bg-ink-100 text-ink-400">
            <X size={16} />
          </button>
        </div>

        <div className="p-4 space-y-5 overflow-y-auto flex-1">

          {/* ── Capas ── */}
          <div>
            <div className={SECTION_TITLE}>Capas</div>
            <div className="space-y-1.5">
              <label className={`${CHECK_ROW} opacity-60`}>
                <input type="checkbox" checked disabled className="rounded border-ink-300" />
                Municipios
              </label>
              <label className={CHECK_ROW}>
                <input
                  type="checkbox"
                  checked={capaProvincias}
                  onChange={() => setCapaProvincias((v) => !v)}
                  className="rounded border-ink-300"
                />
                Provincias
              </label>
              <label className={CHECK_ROW}>
                <input
                  type="checkbox"
                  checked={capaCCAA}
                  onChange={() => setCapaCCAA((v) => !v)}
                  className="rounded border-ink-300"
                />
                Comunidades Autónomas
              </label>
              <label className={CHECK_ROW}>
                <input
                  type="checkbox"
                  checked={capaZonas}
                  onChange={() => setCapaZonas((v) => !v)}
                  className="rounded border-ink-300"
                />
                Zonas comerciales
              </label>
            </div>
          </div>

          {/* ── Zona comercial (solo si la capa está activa) ── */}
          {capaZonas && zonas && zonas.length > 0 && (
            <div>
              <div className={SECTION_TITLE}>Zona comercial</div>
              <select
                value={zonaFilter}
                onChange={(e) => setZonaFilter(e.target.value)}
                className="w-full border border-ink-200 rounded-md px-3 py-2 text-sm bg-white"
              >
                <option value="">Todas las zonas</option>
                {zonas.map((z) => (
                  <option key={z.id} value={z.id}>
                    {z.nombre}{z.titular_nombre ? ` · ${z.titular_nombre}` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* ── Sector del agua (solo mercado) ── */}
          {esMercado && (
            <div>
              <div className={SECTION_TITLE}>Sector del agua</div>
              <div className="space-y-1.5">
                {SECTORES_AGUA.map((s) => (
                  <label key={s.value} className={CHECK_ROW}>
                    <input
                      type="radio"
                      name="sector-agua"
                      checked={sectorAgua === s.value}
                      onChange={() => setSectorAgua(s.value)}
                      className="border-ink-300"
                    />
                    {s.label}
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* ── Rol del operador (solo mercado) ── */}
          {esMercado && (
            <div>
              <div className={SECTION_TITLE}>Rol del operador</div>
              <div className="space-y-1.5">
                {ROLES_OPERADOR.map((r) => (
                  <label key={r.value} className={CHECK_ROW}>
                    <input
                      type="checkbox"
                      checked={rolOperador.includes(r.value)}
                      onChange={() => setRolOperador(toggle(rolOperador, r.value))}
                      className="rounded border-ink-300"
                    />
                    {r.label}
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* ── Naturaleza del operador (solo mercado) ── */}
          {esMercado && (
            <div>
              <div className={SECTION_TITLE}>Naturaleza del operador</div>
              <div className="space-y-1.5">
                {NATURALEZAS_OPERADOR.map((n) => (
                  <label key={n.value} className={CHECK_ROW}>
                    <input
                      type="checkbox"
                      checked={naturalezaOperador.includes(n.value)}
                      onChange={() => setNaturalezaOperador(toggle(naturalezaOperador, n.value))}
                      className="rounded border-ink-300"
                    />
                    {n.label}
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* ── Grupo empresarial (solo mercado) ── */}
          {esMercado && (
            <div>
              <div className={SECTION_TITLE}>Grupo empresarial</div>
              <div className="space-y-1.5">
                {GRUPOS_MERCADO.map((g) => (
                  <label key={g.value} className={CHECK_ROW}>
                    <input
                      type="checkbox"
                      checked={gruposFilter.includes(g.value)}
                      onChange={() => setGruposFilter(toggle(gruposFilter, g.value))}
                      className="rounded border-ink-300"
                    />
                    {g.label}
                  </label>
                ))}
              </div>
              {gruposFilter.length > 0 && (
                <button
                  className="mt-2 text-[11px] text-ink-400 hover:text-ink-600 underline"
                  onClick={() => setGruposFilter([])}
                >
                  Limpiar selección
                </button>
              )}
            </div>
          )}

          {/* ── Tamaño municipio (solo mercado, con counts) ── */}
          {esMercado && (
            <div>
              <div className={SECTION_TITLE}>Tamaño municipio</div>
              <div className="space-y-1.5">
                {RANGOS_POB.map((r) => {
                  const cnt = countsPorRango?.[r.value];
                  return (
                    <label key={r.value} className={CHECK_ROW}>
                      <input
                        type="checkbox"
                        checked={rangoFilter.includes(r.value)}
                        onChange={() => setRangoFilter(toggle(rangoFilter, r.value))}
                        className="rounded border-ink-300"
                      />
                      <span className="flex-1">{r.label}</span>
                      {cnt != null && (
                        <span className="text-[11px] text-ink-400 tabular-nums">({cnt})</span>
                      )}
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Geografía (filtro múltiple) ── */}
          <div>
            <div className={SECTION_TITLE}>Geografía</div>
            <div className="space-y-3">
              <MultiSelect
                label="CCAA"
                options={ccaaOptions}
                value={ccaasFilter}
                onChange={setCcaasFilter}
              />
              <MultiSelect
                label="Provincia"
                options={provinciaOptions}
                value={provinciasFilter}
                onChange={setProvinciasFilter}
              />
              {/* El filtro de tamaño solo aplica en modo comercial (en mercado se usa rango arriba) */}
              {modo === 'comercial' && (
                <select
                  value={tamano}
                  onChange={(e) => setTamano(e.target.value)}
                  className="w-full border border-ink-200 rounded-md px-3 py-2 text-sm bg-white"
                >
                  {TAMANOS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
